import { useState } from 'react';
import type { JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface ScaleFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: number) => void;
  min: number;
  max: number;
}

const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Outline-style scale buttons. Selected = accent border + tinted background
// (no filled primary). Wide ranges (NPS 0–10) collapse to compact 32 px
// buttons; narrow ranges (Likert / CSAT) get 40 px buttons that read as
// primary CTAs.
export function ScaleField({ palette, question, value, onChange, min, max }: ScaleFieldProps): JSX.Element {
  const current = typeof value === 'number' ? value : null;
  const items = Array.from({ length: max - min + 1 }, (_, index) => min + index);
  const { lowLabel, highLabel } = question.settings ?? {};
  const compact = items.length > 6;
  const buttonHeight = compact ? 32 : 40;
  const fontSize = compact ? 13 : 14;
  const radius = compact ? 6 : 8;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          display: 'grid',
          gap: 6,
          gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        }}
      >
        {items.map((number) => (
          <ScaleButton
            key={number}
            number={number}
            selected={current === number}
            palette={palette}
            height={buttonHeight}
            fontSize={fontSize}
            radius={radius}
            onClick={() => onChange(number)}
          />
        ))}
      </div>
      {(lowLabel || highLabel) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            color: palette.textMuted,
          }}
        >
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}

function ScaleButton({
  number,
  selected,
  palette,
  height,
  fontSize,
  radius,
  onClick,
}: {
  number: number;
  selected: boolean;
  palette: Palette;
  height: number;
  fontSize: number;
  radius: number;
  onClick: () => void;
}): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const borderColor = selected
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.primary, 0.6)
      : palette.border;
  const background = selected
    ? hexWithAlpha(palette.primary, 0.08)
    : palette.card;
  const color = selected
    ? palette.primary
    : hovered
      ? palette.text
      : palette.textMuted;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="chirp-btn chirp-survey-scale-button"
      style={{
        height,
        borderRadius: radius,
        border: `1px solid ${borderColor}`,
        background,
        color,
        fontSize,
        fontWeight: 500,
        cursor: 'pointer',
        padding: 0,
        transition: `border-color 220ms ${PREMIUM_EASE}, background-color 220ms ${PREMIUM_EASE}, color 220ms ${PREMIUM_EASE}`,
      }}
    >
      {number}
    </button>
  );
}
